import { VerificationStatus, isPendingLikeStatus } from "../common/constant";
import { isStandaloneTier, tierIdOf } from "./tier";

export { isStandaloneTier };

/**
 * Standalone verifications — the tier-level-0 `type: "standalone"` checks a case
 * asks a client for, outside the 1..N tier ladder. A case can ask for SEVERAL at
 * once, so every one of them has to be listed and completable on its own.
 *
 * Lives here rather than in the layouts because all six view files
 * (3 web + 3 mobile) need the same answer, and the previous one-liner had
 * already been copied into all six — the layout-drift footgun the module README
 * opens with.
 */

export const selectStandaloneTiers = (tiers = []) =>
    (tiers || []).filter(isStandaloneTier);

const newestHistoryFor = (tierId, histories = []) =>
    [...(histories || [])]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .find((h) => tierIdOf(h) === tierId) || null;

// Statuses where the client can no longer act on a standalone.
//
// `verified` is finished. `verification_expired` is closed because the deadline
// is what expired: the case itself moves to `expired` once any action passes its
// expiredAt, so there is nothing left for the client to do and re-issuing is an
// operator's job -- it must not keep the tier ladder hidden for ever.
//
// `canceled` is deliberately NOT here. The case stays await_user, so the client
// still owes it, and the tier card already offers a "start a new record" button
// for exactly that -- treating it as closed would hide that button and strand
// them.
const CLOSED_STATUSES = [VerificationStatus.VERIFIED, VerificationStatus.EXPIRED];

/**
 * Whether this standalone still needs the client to do something.
 *
 * No record yet counts as outstanding: the case asked for it, and selecting the
 * tier is what creates the record. Pending-like (`verification_submitted` /
 * `pending_verification`) does NOT -- submitted is as far as a client can take
 * it, and the rest is review. `verification_failed` does, because a failed check
 * is the one thing they are expected to redo.
 */
export const isStandaloneOutstanding = (tier, histories = []) => {
    const history = newestHistoryFor(tier?._id, histories);
    if (!history) return true;
    if (isPendingLikeStatus(history.status)) return false;
    return !CLOSED_STATUSES.includes(history.status);
};

/**
 * What the tier screen should render.
 *
 * While any standalone is outstanding the ladder is suppressed and the
 * standalone cards take over -- ALL of them, including ones already submitted,
 * so the client sees the whole of what was asked rather than one card that
 * quietly gets replaced by the next. Once none is outstanding the ladder comes
 * back; the previous behaviour hid it permanently, so a brand with a standalone
 * tier could never show tier upgrades again.
 *
 * `ladderTiers` deliberately excludes standalone tiers. resolveActionableTier
 * walks by `tierLevel` and keeps one record per level, but every standalone sits
 * at level 0 -- so feeding them in lets two standalone tiers collide on one
 * history, and lets a closed standalone become the ladder's "actionable" tier.
 * The ladder cursor should only ever be about the ladder.
 */
export const resolveTierListing = (tiers = [], histories = []) => {
    const standaloneTiers = selectStandaloneTiers(tiers);
    const outstandingStandalone = standaloneTiers.filter((tier) =>
        isStandaloneOutstanding(tier, histories),
    );

    return {
        standaloneTiers,
        outstandingStandalone,
        showStandalone: outstandingStandalone.length > 0,
        ladderTiers: (tiers || []).filter((tier) => !isStandaloneTier(tier)),
    };
};
